import useAPI, {
  useAPIMutation,
  useDistinctFilterOptions
} from "@/services/api/hook";
import { useQueryClient } from "@tanstack/react-query";
import {
  APIObject,
  ExtendedUseQueryResult,
  Filters,
  SearchQuery
} from "@/services/api/types";
import {
  CompaniesPage,
  CompanyCommercialsResponse,
  CompanyData,
  CompanyIndustriesResponse,
  CompanySegmentsResponse
} from "./types";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";
import { useEffect } from "react";
import { refreshEntityDetail, useEntities } from "@/services/api/entityhooks";
import { EntitySliceState } from "@/services/api/entitystore";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import {
  useEntitiesWithInfinite,
  UseEntitiesWithInfiniteOptions
} from "@/services/api/entityinfinite";
import { ComputedFields } from "@/services/api/types";

export const MAX_COMPANIES_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const useCompanies = (
  ids: string[],
  options: {
    enabled?: boolean;
    computed_fields?: ComputedFields;
    forceRefetch?: boolean;
    include_additionals?: boolean;
  } = {}
) => {
  const forceRefetch = options.forceRefetch ?? false;
  const enabled = options.enabled;
  const include_additionals = options.include_additionals;

  const useCompaniesStore = (): EntitySliceState<CompanyData> => {
    return useBoundStore((state) => state.companies);
  };

  return useEntities<CompanyData>(
    ids,
    "companies",
    useCompaniesStore,
    (endpoint, params, apiOptions) =>
      useAPI(
        endpoint,
        {
          ...params,
          queryParams: {
            ...(options.computed_fields && {
              computed_fields: options.computed_fields
            }),
            ...(include_additionals && {
              include_additionals: "true"
            })
          }
        },
        { ...apiOptions, enabled }
      ),
    undefined,
    options.computed_fields,
    forceRefetch
  );
};

export const useCompaniesWithInfinite = (
  ids: string[] = [],
  options: UseEntitiesWithInfiniteOptions = {}
) => {
  return useEntitiesWithInfinite<CompanyData>(ids, useCompaniesInfinite, {
    enabled: options.enabled,
    fetchAllPages: options.fetchAllPages,
    sortField: options.sortField || "name",
    sortAsc: options.sortAsc ?? true,
    pageSize: MAX_COMPANIES_PER_PAGE
  });
};

export const useAllCompanies = (
  page: number = 1,
  size: number = MAX_COMPANIES_PER_PAGE
): ExtendedUseQueryResult<CompaniesPage> => {
  const result = useAPI("companies/all", {
    queryParams: { page: page.toString(), size: size.toString() }
  });

  const addCompany = useBoundStore((state) => state.companies.addEntity);

  useEffect(() => {
    if (result.data?.companies) {
      result.data.companies.forEach(addCompany);
    }
  }, [result.data, addCompany]);

  return result;
};

export const useUpdateCompanyMutation = () => {
  const updateCompany = useBoundStore((state) => state.companies.updateEntity);
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { companyId: string }; data: CompanyData },
    CompanyData
  >(
    "companies/update",
    {
      method: "PUT"
    },
    (response) => {
      updateCompany(response.id, response as Partial<CompanyData>);
      // Invalidate React Query cache so next fetch gets fresh data
      refreshEntityDetail(queryClient, "companies", response.id);
    }
  );
};

export const useCreateCompanyMutation = () => {
  const addCompany = useBoundStore((state) => state.companies.addEntity);
  return useAPIMutation<CompanyData, APIObject>(
    "createCompany",
    {
      method: "POST"
    },
    (response, data) => {
      addCompany({
        ...data,
        id: response.id
      } as CompanyData);
    }
  );
};

export interface CreateWithDreamerResponse {
  domain: string;
}

/** Resolves on 202, before the company exists — no store update; the outcome arrives as a Dreamer notification. */
export const useCreateCompanyWithDreamerMutation = () =>
  useAPIMutation<{ website: string }, CreateWithDreamerResponse>(
    "createCompanyWithDreamer",
    { method: "POST" }
  );

export const {
  useFilteredEntitiesInfinite,
  useResetFilteredEntitiesInfinite: useResetCompaniesInfinite
} = createUseFilteredEntitiesInfinite<CompanyData>(
  "companies",
  MAX_COMPANIES_PER_PAGE
);

export const useCompaniesInfinite = (
  filters?: Filters,
  search?: SearchQuery,
  groupedBy?: keyof CompanyData,
  pageSize = MAX_COMPANIES_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll?: boolean,
  enabled = true,
  computed_fields: ComputedFields = ComputedFields.ALL
) => {
  return useFilteredEntitiesInfinite(
    () => ({ addEntity: useBoundStore((state) => state.companies.addEntity) }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      enabled,
      computed_fields
    }
  );
};

export const useDeleteCompanyMutation = () => {
  const removeCompany = useBoundStore((state) => state.companies.removeEntity);
  return useAPIMutation<{ pathParams: { companyId: string } }, void>(
    "companies/id",
    {
      method: "DELETE"
    },
    (_response, data) => {
      removeCompany(data.pathParams.companyId);
    }
  );
};

export const useCompanyIndustries =
  (): ExtendedUseQueryResult<CompanyIndustriesResponse> => {
    const storedIndustries = useBoundStore(
      (state) => state.lookup.data.companyIndustries
    );
    const setCompanyIndustries = useBoundStore(
      (state) => state.lookup.setCompanyIndustries
    );

    const result = useAPI(
      "companies/industries",
      {},
      {
        enabled: !storedIndustries
      }
    );

    useEffect(() => {
      if (result.isSuccess && result.data?.industries && !storedIndustries) {
        setCompanyIndustries(result.data.industries);
      }
    }, [result.data, result.isSuccess, setCompanyIndustries, storedIndustries]);

    if (storedIndustries) {
      return {
        ...result,
        data: { industries: storedIndustries },
        isSuccess: true,
        isLoading: false,
        isFetching: false
      } as ExtendedUseQueryResult<CompanyIndustriesResponse>;
    }

    return result;
  };

export const useCompanySegments =
  (): ExtendedUseQueryResult<CompanySegmentsResponse> => {
    const storedSegments = useBoundStore(
      (state) => state.lookup.data.companySegments
    );
    const setCompanySegments = useBoundStore(
      (state) => state.lookup.setCompanySegments
    );

    const result = useAPI(
      "companies/segments",
      {},
      {
        enabled: !storedSegments
      }
    );

    useEffect(() => {
      if (result.isSuccess && result.data?.segments && !storedSegments) {
        setCompanySegments(result.data.segments);
      }
    }, [result.data, result.isSuccess, setCompanySegments, storedSegments]);

    if (storedSegments) {
      return {
        ...result,
        data: { segments: storedSegments },
        isSuccess: true,
        isLoading: false,
        isFetching: false
      } as ExtendedUseQueryResult<CompanySegmentsResponse>;
    }

    return result;
  };

export const useCompaniesDistinctFilterOptions = (
  fieldName: string,
  filters?: Filters,
  searchTerm?: string
) => {
  return useDistinctFilterOptions("companies", fieldName, filters, searchTerm);
};

export interface DuplicateMatch {
  companyId: string;
  companyName: string;
  domain: string;
  matchType: string;
}

export interface DuplicateCheckResponse {
  exactMatches: DuplicateMatch[];
  similarMatches: DuplicateMatch[];
  // More companies matched the domain than the backend examined, so the lists —
  // and any count taken from them — are a lower bound. Optional so the dialog
  // keeps working against a companiesapi that predates the field.
  truncated?: boolean;
}

export const useCheckCompanyDuplicates = (
  website: string,
  enabled: boolean = true
): ExtendedUseQueryResult<DuplicateCheckResponse> => {
  const trimmedWebsite = website.trim();
  return useAPI(
    "companies/check-duplicates",
    {
      method: "POST",
      data: { website: trimmedWebsite }
    },
    { enabled: enabled && trimmedWebsite.length > 0 }
  );
};

export const useCompanyByName = (
  name: string,
  enabled: boolean = true
): ExtendedUseQueryResult<CompaniesPage> => {
  return useAPI(
    "companies/filter",
    {
      method: "POST",
      queryParams: { size: "1" },
      data: {
        filters: { name: { in_nocase: [name.trim()] } }
      }
    },
    { enabled: enabled && name.trim().length > 0 }
  );
};

export const useSearchCompaniesByName = (
  name: string,
  enabled: boolean = true
): ExtendedUseQueryResult<CompaniesPage> => {
  const trimmedName = name.trim();
  return useAPI(
    "companies/filter",
    {
      method: "POST",
      queryParams: { size: "5" },
      data: {
        filters: { name: { contains_nocase: trimmedName } }
      }
    },
    { enabled: enabled && trimmedName.length >= 2 }
  );
};

export const useCompanyCommercials = (
  companyId: string
): ExtendedUseQueryResult<CompanyCommercialsResponse> =>
  useAPI("companies/commercials", { pathParams: { companyId } });

export const useCompanyPastContracts = (
  companyId: string,
  groupBy?: string
): ExtendedUseQueryResult<CompanyCommercialsResponse> =>
  useAPI("companies/pastContracts", {
    pathParams: { companyId },
    queryParams: groupBy ? { groupBy } : undefined
  });

export default {
  useCompanies,
  useAllCompanies,
  useUpdateCompanyMutation,
  useCreateCompanyMutation,
  useCompaniesInfinite,
  useResetCompaniesInfinite
};
